import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/login')({ component: Login });

function Login(): React.JSX.Element {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuthStore();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const isSignUp = mode === 'signup';

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (isSignUp) {
        const signedIn = await signUp(email, password, name || undefined);
        if (!signedIn) {
          setConfirmationSent(true);
          return;
        }
      } else {
        await signIn(email, password);
      }
      await navigate({ to: '/home' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-zinc-50 px-4 py-10 dark:bg-zinc-950">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-center gap-2">
          <span className="grid size-8 place-items-center rounded-md bg-foreground text-sm font-bold text-background">P</span>
          <span className="text-sm font-semibold">PersonalizeHQ</span>
        </div>

        <section className="rounded-lg border bg-background p-6 shadow-sm">
          {confirmationSent ? (
            <div className="py-4 text-center">
              <CheckCircle2 className="mx-auto size-7 text-emerald-600" />
              <h1 className="mt-4 text-lg font-semibold">Check your email</h1>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                We sent a confirmation link to <span className="font-medium text-foreground">{email}</span>.
              </p>
              <Button variant="outline" className="mt-6 w-full" onClick={() => { setConfirmationSent(false); setMode('signin'); }}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <>
              <div>
                <h1 className="text-xl font-semibold">{isSignUp ? 'Create your workspace' : 'Sign in'}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {isSignUp ? 'Start building mobile customer experiences.' : 'Continue to your PersonalizeHQ workspace.'}
                </p>
              </div>

              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                {isSignUp ? (
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input id="name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  />
                </div>
                {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? 'Working…' : isSignUp ? 'Create account' : 'Sign in'}
                  {!busy ? <ArrowRight className="size-4" /> : null}
                </Button>
              </form>

              <button
                type="button"
                className="mt-5 w-full text-sm text-muted-foreground hover:text-foreground"
                onClick={() => { setMode(isSignUp ? 'signin' : 'signup'); setError(null); }}
              >
                {isSignUp ? 'Already have an account? Sign in' : 'New to PersonalizeHQ? Create an account'}
              </button>
            </>
          )}
        </section>
        <p className="mt-4 text-center text-xs text-muted-foreground">Mobile-native authoring and delivery</p>
      </div>
    </main>
  );
}
